import React, {useMemo, useState} from 'react';
import {Typography} from '@mui/material';
import {t} from 'i18next';
import AurProgressChart, {
  ProgressChartDataSetType,
} from '../../../common/aur-progress-chart/AurProgressChart';
import AurTooltip from '../../../common/aur-tooltip/AurTooltip';
import {ReactComponent as InfoIcon} from '../../../../../src/assests/svg/info_icon.svg';
import {CustodianType} from '../../HostingVolumeReport/ByCustodianTab';
import AurDataGridErrorOverlay from '../../../common/aur-data-grid-error-overlay/AurDataGridErrorOverlay';
import {convertToDecimalPlace} from '../../../../utils/inTakeAuditReportUtils';

const ReportDataTypeColorMap = {
  email: '#6E87FF',
  externallyProcessed: '#00D0DB',
  otherData: '#9F75DF',
};

const ReportDataTypeCaptionMap = {
  email: 'reports.hostingVolume.cumulativeReport.email',
  externallyProcessed:
    'reports.hostingVolume.cumulativeReport.externallyProcessed',
};

// Map each Cumulative Volume report to its corresponding custodian property name
const CustodianPropertyMap: Record<
  TotalCumulativeVolumeReportKey,
  keyof CustodianType
> = {
  email: 'emailCompressed',
  externallyProcessed: 'externallyProcessed',
};

type TotalCumulativeVolumeReportKey = 'email' | 'externallyProcessed';

interface TotalCumulativeVolumeReportType {
  email: string;
  externallyProcessed: string;
}

interface CumulativeVolumeReportChartProp {
  totalCumulativeReceivedVolume: TotalCumulativeVolumeReportType;
  custodianData: CustodianType[];
  hoverCustodian?: CustodianType | null;
  isFailedToLoadContent?: boolean;
  onRefresh?: () => void;
  containerHeight?: number;
}

const CumulativeVolumeReportChart: React.FC<
  CumulativeVolumeReportChartProp
> = ({
  totalCumulativeReceivedVolume,
  custodianData,
  hoverCustodian = null,
  isFailedToLoadContent,
  onRefresh,
  containerHeight,
}) => {
  const [tabName, setTabName] = useState<string>('');
  const chartDataSet: ProgressChartDataSetType[] = useMemo(() => {
    // Initialize reportMap with total values and initial progress
    const reportMap: {[key: string]: ProgressChartDataSetType} = (
      Object.keys(
        totalCumulativeReceivedVolume
      ) as TotalCumulativeVolumeReportKey[]
    ).reduce(
      (acc, key) => ({
        ...acc,
        [key]: {
          total: parseInt(totalCumulativeReceivedVolume[key]),
          progress: 0,
          filledColor: ReportDataTypeColorMap[key],
          key,
        },
      }),
      {} as {[key: string]: ProgressChartDataSetType}
    );

    // Update progress for each custodian based on reportMap keys
    (hoverCustodian ? [hoverCustodian] : custodianData).forEach((custodian) => {
      Object.keys(reportMap).forEach((reportKey) => {
        const custodianProperty =
          CustodianPropertyMap[reportKey as TotalCumulativeVolumeReportKey];
        setTabName(custodian.custodianName);
        if (custodianProperty) {
          reportMap[reportKey].progress += parseInt(
            custodian[custodianProperty] ?? '0'
          );
        }
      });
    });

    return Object.values(reportMap).map((item) => {
      return {
        ...item,
        tooltip: {
          filled: (
            <Typography className="typo-text label2_size tooltip-content">
              {custodianData[0].custodianName === 'gb'
                ? t(
                    'reports.hostingVolume.cumulativeReport.barChartToolTip.count',
                    {
                      value: convertToDecimalPlace(item.progress.toString()),
                    }
                  )
                : t(
                    'reports.hostingVolume.cumulativeReport.barChartToolTip.countDocs',
                    {
                      value: convertToDecimalPlace(item.progress.toString()),
                    }
                  )}
              <br />
              {t(
                'reports.hostingVolume.cumulativeReport.barChartToolTip.grandTotal',
                {
                  total: convertToDecimalPlace(item.total.toString()),
                }
              )}
            </Typography>
          ),
          unFilled: (
            <Typography className="typo-text label2_size tooltip-content">
              {t(
                `reports.hostingVolume.cumulativeReport.barChartToolTip.${item.key}`
              )}
              <br />
              {custodianData[0].custodianName === 'gb'
                ? t(
                    'reports.hostingVolume.cumulativeReport.barChartToolTip.count',
                    {
                      value: convertToDecimalPlace(
                        (item.total - item.progress).toString()
                      ),
                    }
                  )
                : t(
                    'reports.hostingVolume.cumulativeReport.barChartToolTip.countDocs',
                    {
                      value: convertToDecimalPlace(
                        (item.total - item.progress).toString()
                      ),
                    }
                  )}
              <br />
              {t(
                'reports.hostingVolume.cumulativeReport.barChartToolTip.grandTotal',
                {
                  total: convertToDecimalPlace(item.total.toString()),
                }
              )}
            </Typography>
          ),
        },
      };
    });
  }, [custodianData, totalCumulativeReceivedVolume, hoverCustodian]);

  const renderReportDataGuideItem = (
    key: TotalCumulativeVolumeReportKey,
    caption: string,
    tooltip?: string
  ) => {
    const totalValue: string = totalCumulativeReceivedVolume[key];
    return (
      <div className="guide-content-card">
        <div className="row-full-width">
          <Typography
            aria-label={t(caption)}
            className="typo-text body4_size title-label"
          >
            {t(caption)}
          </Typography>
          {tooltip ? (
            <span className="tooltip-padding">
              <AurTooltip
                tabIndex={0}
                title={t(tooltip)}
                aria-label={t(tooltip)}
                className="typo-text body4_size tooltop-content"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -7],
                        },
                      },
                    ],
                  },
                }}
              >
                <InfoIcon />
              </AurTooltip>
            </span>
          ) : null}
        </div>
        <div className="guide-chart-row">
          <div className="bar-chart-container">
            <AurProgressChart
              dataSet={[
                {
                  total: parseInt(totalValue),
                  progress: parseInt(totalValue),
                  filledColor: ReportDataTypeColorMap[key],
                  key: key,
                },
              ]}
              layout={{
                barWidth: 5,
                barHeight: 19,
                spaceBetweenBar: 1,
              }}
            />
          </div>
          <Typography
            aria-label={`Total ${totalCumulativeReceivedVolume[key]}  ${t('reports.hostingVolume.cumulativeReport.gbFor')} ${t(caption)}`}
            className="typo-text headline1_size volume-text"
          >
            {convertToDecimalPlace(totalCumulativeReceivedVolume[key] ?? '0')}
            <span className="typo-text label1_size gb-caption">
              {tabName === 'gb'
                ? t('reports.hostingVolume.cumulativeReport.gb')
                : t('reports.hostingVolume.cumulativeReport.docs')}
            </span>
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <div className="cumulative-volume-card">
      <div className="header-content-row">
        <div className="title-row">
          <Typography
            aria-label={t('reports.hostingVolume.cumulativeReport.title')}
            className="typo-text subhead2_size title-color"
          >
            {t('reports.hostingVolume.cumulativeReport.title')}
          </Typography>
          <Typography
            aria-label={
              tabName === 'gb'
                ? t('reports.hostingVolume.cumulativeReport.subTitle')
                : t('reports.hostingVolume.cumulativeReport.subTitleDocs')
            }
            className="typo-text body3_size sub-title-text"
          >
            {tabName === 'gb'
              ? t('reports.hostingVolume.cumulativeReport.subTitle')
              : t('reports.hostingVolume.cumulativeReport.subTitleDocs')}
          </Typography>
        </div>
        {!isFailedToLoadContent && (
          <div className="chart-guide-row">
            {renderReportDataGuideItem(
              'email',
              ReportDataTypeCaptionMap['email'],
              'reports.hostingVolume.cumulativeReport.emailInfo'
            )}
            {renderReportDataGuideItem(
              'externallyProcessed',
              ReportDataTypeCaptionMap['externallyProcessed'],
              'reports.hostingVolume.cumulativeReport.externallyProcessedInfo'
            )}
          </div>
        )}
      </div>
      <div className="aur-progress-chart-section">
        {isFailedToLoadContent && onRefresh && containerHeight ? (
          <AurDataGridErrorOverlay
            onRefresh={onRefresh}
            containerHeight={containerHeight}
          />
        ) : (
          <AurProgressChart dataSet={chartDataSet} />
        )}
      </div>
    </div>
  );
};

export default CumulativeVolumeReportChart;
